/**
 * ===========================================================================================
 * DATABASE SYNCHRONIZATION UTILITY
 * ===========================================================================================
 *
 * PURPOSE:
 * Ensures the database stores the most current aggregated calculations from the UI.
 * This maintains data consistency between frontend calculations and backend persistence.
 *
 * BUSINESS CONTEXT:
 * When users complete value assessments, the frontend performs real-time calculations
 * using the formulaEngine. This function persists those authoritative results to the
 * database for reporting, exports, and audit trails.
 *
 * CALCULATION FLOW:
 * Frontend (formulaEngine) → syncTaskAggregationToDatabase() → Database (authoritative)
 *
 * PARAMETERS:
 * @param projectId - Unique project identifier (UUID format)
 * @param annualSavings - Total first-year savings across all activities (USD)
 * @param fiveYearValue - Cumulative 5-year savings projection (USD)
 * @param totalFTEs - Total full-time equivalents across all regions
 * @param taskCount - Number of individual activities/tasks analyzed
 */
import { apiRequest } from '@/lib/queryClient';

export const syncTaskAggregationToDatabase = async (
    projectId: string,
    annualSavings: number,
    fiveYearValue: number,
    totalFTEs: number,
    taskCount: number
) => {
    try {
        // In Cypress test runs, skip syncing to avoid unintended network calls during cleanup-only scenarios
        // Detect Cypress by presence of window.Cypress at runtime
        if (typeof window !== 'undefined' && (window as any).Cypress) {
            return;
        }

        // POST to dedicated sync endpoint with aggregated calculation results (authenticated)
        await apiRequest(`/api/projects/${projectId}/sync-task-aggregation`, {
            method: 'POST',
            body: {
                annualSavings,        // Authoritative annual savings calculation
                fiveYearValue,        // Projected cumulative value (5 years)
                totalFTEs,            // Sum of all regional FTE allocations
                taskCount             // Count of analyzed activities/processes
            }
        });
    } catch (error: unknown) {
        console.error('❌ SYNC ERROR: Task aggregation sync failed', error);
    }
};
